import AppForm from '@/components/AppForm';
import { UnsavedChangesModal } from '@/components/AppModal/modals';
import FormUpload from '@/components/common/FormUpload';
import PATH from '@/config/paths/PATH';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import { formatNumber } from '@/utils/formatters';
import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Switch,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

function PieceCreate({ openPieceModal, setOpenPieceModal, setPieceList }) {
  const [form] = Form.useForm();
  const pieceRefs = useRef({});
  const [lastAddedId, setLastAddedId] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [localList, setLocalList] = useState([]);

  useEffect(() => {
    if (openPieceModal && localList.length === 0) {
      const newId = uuidv4();
      setLocalList((prev) => [...prev, { id: newId }]);
    }
  }, [openPieceModal, localList]);

  useEffect(() => {
    if (lastAddedId && pieceRefs.current[lastAddedId]) {
      pieceRefs.current[lastAddedId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedId(lastAddedId);
      setLastAddedId(null);
    }

    const timer = setTimeout(() => setHighlightedId(null), 1000);
    return () => clearTimeout(timer);
  }, [localList, lastAddedId]);

  const onSubmit = async () => {
    if (localList.length > 0) {
      const values = form.getFieldsValue();
      const pieces = localList.map((item) => {
        const imageFileList = values[`image-path-piece-${item.id}`];
        const imageFile = imageFileList?.[0]?.originFileObj || imageFileList?.[0];

        return {
          id: item.id,
          name: values[`name-piece-${item.id}`],
          ordinal_number: values[`ordinal-number-piece-${item.id}`],
          percent: values[`percent-piece-${item.id}`],
          quantity_piece: values[`quantity-piece-${item.id}`],
          image_file: imageFile,
          is_active: values[`is-active-piece-${item.id}`] ? true : false,
          is_limited: values[`is-limited-piece-${item.id}`] ? true : false,
          is_new: true,
        };
      });
      setPieceList((prev) => [...prev, ...pieces]);
      form.setFieldsValue();
      setLocalList([]);
      setOpenPieceModal(false);
    }
  };

  const handleCancel = () => {
    if (form.isFieldsTouched()) {
      UnsavedChangesModal(async () => {
        await setOpenPieceModal(false);
        setLocalList([]);
      });
    } else {
      setOpenPieceModal(false);
      setLocalList([]);
    }
  };
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  return (
    <Drawer
      open={openPieceModal}
      height={'60%'}
      width={isMobile ? '100%' : '70%'}
      maskClosable={false}
      closable
      onClose={handleCancel}
      title="Create Piece"
      extra={
        <Button
          className="w-full"
          type="default"
          icon={<PlusOutlined />}
          onClick={() => {
            const newId = uuidv4();
            setLocalList((prev) => [...prev, { id: newId }]);
            setLastAddedId(newId);
          }}
        >
          Add Piece
        </Button>
      }
      footer={
        <Space className="w-full justify-end">
          <Space className="w-full justify-end">
            <Button type="text" className="font-medium" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="font-medium"
              onClick={() => form.submit()}
              // disabled={localList.length === 0 || !form.isFieldsTouched()}
            >
              Create
            </Button>
          </Space>
        </Space>
      }
    >
      {localList.length > 0 ? (
        localList?.map((item, index) => (
          <Space
            className={`w-full ${highlightedId === item.id ? 'animate-highlight' : 'card-highlight'} rounded-md max-[768px]:py-0 p-4`}
            direction="vertical"
            key={`piece-create-${index}`}
            ref={(el) => (pieceRefs.current[item.id] = el)}
          >
            <Space className="w-full items-center">
              <p className="font-semibold text-lg">{`Piece ${index + 1}`}</p>
            </Space>
            <AppForm
              form={form}
              initialValues={item}
              onSubmit={onSubmit}
              targetPath={PATH.PRIZE_MANAGEMENT.ROOT}
              onCancel={() => {
                setOpenPieceModal(false);
              }}
              className=""
              hasButtons={false}
              isCard={false}
            >
              <Row gutter={24}>
                <Col span={isMobile ? 24 : 20}>
                  <Form.Item
                    label="Piece Name"
                    name={`name-piece-${item.id}`}
                    rules={[{ message: 'Please enter piece name', required: true }]}
                  >
                    <Input placeholder="Enter piece name" />
                  </Form.Item>
                  <Form.Item
                    label="Piece Position"
                    name={`ordinal-number-piece-${item.id}`}
                    rules={[{ message: 'Please enter piece position', required: true }]}
                  >
                    <Input type="number" placeholder="Enter piece position" min={1} />
                  </Form.Item>

                  <Form.Item
                    label="Drop Rate"
                    name={`percent-piece-${item.id}`}
                    rules={[{ message: 'Please enter drop rate', required: true }]}
                  >
                    <Input
                      type="number"
                      addonBefore="%"
                      className="w-full"
                      placeholder="Enter drop rate"
                      min={0}
                      max={100}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Quantity"
                    name={`quantity-piece-${item.id}`}
                    rules={[{ message: 'Please enter quantity', required: true }]}
                  >
                    <InputNumber
                      className="w-full"
                      placeholder="Enter quantity"
                      min={0}
                      formatter={(value) => formatNumber(value)}
                      parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Image"
                    name={`image-path-piece-${item.id}`}
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                      if (Array.isArray(e)) {
                        return e;
                      }
                      return e?.fileList;
                    }}
                    rules={[{ message: 'Please upload image', required: true }]}
                    initialValue={[]}
                  >
                    <FormUpload beforeUpload={() => false} />
                  </Form.Item>
                </Col>
                <Col span={isMobile ? 24 : 4}>
                  <Form.Item
                    label="Is Limited"
                    name={`is-limited-piece-${item.id}`}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    label="Is Active"
                    name={`is-active-piece-${item.id}`}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </AppForm>
            <Divider
              dashed
              style={{
                margin: '0 0 12px 0',
              }}
            />
          </Space>
        ))
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={'No pieces added yet. Click "Add Piece" to start.'}
        ></Empty>
      )}
    </Drawer>
  );
}

export default PieceCreate;
